import {
    Button,
    ButtonBase,
    Divider,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid,
    Hidden,
    Switch,
    Typography,
    useMediaQuery,
} from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import clsx from 'clsx'
import _ from 'lodash'
import React, { useState } from 'react'
import { FormSpy } from 'react-final-form'
import { Trans } from 'react-i18next'
import NoAccess from '../../../common/noAccess'
import { useFlippable } from '../../../helpers/useFlippable'
import PadLockIcon from '../../../icons/padLockIcon'
import UpArrowCircleIcon from '../../../icons/upArrowCircleIcon'
import SortedImagesField from '../../components/sortedImagesField'
import TextField from '../../components/textField'
import { useFormsStyles } from '../../forms.css'
import DraggableImage from '../../menu/components/draggableImage'
import { AddFormImageState } from '../addForm'
import ShareImage from './shareImage'
import { GridSize } from '@material-ui/core/Grid/Grid'
import { Breakpoint } from '@material-ui/core/styles/createBreakpoints'

interface Props extends Partial<Record<Breakpoint, boolean | GridSize>> {
    canPublishMultiplePhoto: boolean
    showInstagram: boolean
    showTwitter: boolean
    mutators: Record<string, (...args: any[]) => any>
}

const PhotoContainer: React.FC<Props> = ({
    showInstagram,
    showTwitter,
    canPublishMultiplePhoto,
    mutators,
    ...gridSize
}) => {
    const theme = useTheme()
    const isMobileScreen = useMediaQuery(theme.breakpoints.down('xs'), { noSsr: true })

    const formsClasses = useFormsStyles()
    const [displayOptions, setDisplayOptions] = useState(false)
    const { onDisplayImagesOptions, shouldDisplayImagesOptions } = useFlippable()

    return (
        <FormSpy subscription={{ values: true }}>
            {(props) => (
                <SortedImagesField<AddFormImageState>
                    name="photos"
                    errorName="photo"
                    maxFiles={canPublishMultiplePhoto ? 10 : 1}
                    defaultProperties={{
                        id: 0,
                        url: '',
                        sortOrder: 0,
                        isFacebookShared: props.values?.isFacebookShared ?? true,
                        isInstagramShared: props.values?.isInstagramShared ?? true,
                        isGoogleShared: props.values?.isGoogleShared ?? true,
                        isTwitterShared: props.values?.isTwitterShared ?? true,
                    }}
                    onChange={(images) => {
                        let imagesTwitterShared = 0
                        const sharedImages = images.filter((image) => {
                            if (image.isTwitterShared) {
                                imagesTwitterShared++
                            }
                            return imagesTwitterShared > 4 && image.isTwitterShared
                        })
                        const sharedImagesToDisableId = sharedImages.map((sharedImage) => {
                            return sharedImage.id
                        })
                        return images.map((image) => {
                            if (sharedImagesToDisableId.includes(image.id)) {
                                return {
                                    ...image,
                                    isTwitterShared: false,
                                }
                            }
                            return image
                        })
                    }}
                >
                    {(images, Uploader, hasError, error) => (
                        <>
                            <Typography
                                color="textSecondary"
                                className={clsx(formsClasses.formTitle, formsClasses.noPaddingTop, {
                                    [formsClasses.error]: hasError,
                                })}
                            >
                                {canPublishMultiplePhoto ? (
                                    <Trans i18nKey="form.photosImage" values={{ count: images.length }} />
                                ) : (
                                    <>
                                        <Trans i18nKey="form.photoImage" />
                                        <NoAccess>
                                            {(onClick) => (
                                                <Button
                                                    variant="outlined"
                                                    className={formsClasses.multiplePhoto}
                                                    onClick={onClick}
                                                >
                                                    <Trans i18nKey="views.slate.add.multiplePhoto" />
                                                    <PadLockIcon
                                                        fontSize="small"
                                                        color="inherit"
                                                        className={clsx(
                                                            formsClasses.permissionsIcon,
                                                            formsClasses.lockIcon,
                                                        )}
                                                    />
                                                    <UpArrowCircleIcon
                                                        fontSize="small"
                                                        color="primary"
                                                        className={formsClasses.permissionsIcon}
                                                    />
                                                </Button>
                                            )}
                                        </NoAccess>
                                    </>
                                )}
                                <Hidden smUp>
                                    {images.length > 1 && (
                                        <FormControlLabel
                                            className={formsClasses.options}
                                            control={
                                                <Switch
                                                    size="small"
                                                    color="primary"
                                                    checked={displayOptions}
                                                    onChange={(event, checked) => setDisplayOptions(checked)}
                                                />
                                            }
                                            label={<Trans i18nKey="form.photosImageOptions" />}
                                        />
                                    )}
                                </Hidden>
                            </Typography>
                            <FormControl margin="normal" error={hasError}>
                                <Grid container spacing={2}>
                                    {_.sortBy(images, ['sortOrder']).map((image, index) => (
                                        <DraggableImage
                                            error={hasError}
                                            key={index}
                                            index={index}
                                            url={image.url}
                                            moveEntry={mutators.moveEntry}
                                            deleteEntry={() => mutators.deleteImage(image)}
                                            title=""
                                            {...gridSize}
                                            ImageContainer={({ className, children }) => (
                                                <div className={className}>
                                                    {shouldDisplayImagesOptions(image.id) ||
                                                    images.length === 1 ? (
                                                        <div
                                                            className={formsClasses.imageContainer}
                                                            children={children}
                                                        />
                                                    ) : (
                                                        <ButtonBase
                                                            className={formsClasses.imageContainer}
                                                            onClick={() =>
                                                                onDisplayImagesOptions(
                                                                    image.id,
                                                                    !shouldDisplayImagesOptions(image.id),
                                                                )
                                                            }
                                                            children={children}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                            containerClassName={clsx({
                                                [formsClasses.flippableContainer]: !isMobileScreen,
                                                [formsClasses.flip]: shouldDisplayImagesOptions(image.id),
                                            })}
                                            imageContainerClassName={clsx(
                                                formsClasses.flippable,
                                                formsClasses.flipFront,
                                            )}
                                            beforeImage={
                                                <Hidden xsDown>
                                                    {images.length > 1 && (
                                                        <FormControlLabel
                                                            className={clsx(
                                                                formsClasses.options,
                                                                formsClasses.imageOptions,
                                                            )}
                                                            control={
                                                                <Switch
                                                                    color="primary"
                                                                    checked={shouldDisplayImagesOptions(
                                                                        image.id,
                                                                    )}
                                                                    onChange={(event, checked) =>
                                                                        onDisplayImagesOptions(
                                                                            image.id,
                                                                            checked,
                                                                        )
                                                                    }
                                                                />
                                                            }
                                                            label={
                                                                <Trans i18nKey="form.photosImageOptions" />
                                                            }
                                                        />
                                                    )}
                                                </Hidden>
                                            }
                                        >
                                            {images.length > 1 && (
                                                <div
                                                    className={clsx(
                                                        formsClasses.flippable,
                                                        formsClasses.flipBack,
                                                        {
                                                            [formsClasses.show]: displayOptions,
                                                        },
                                                    )}
                                                >
                                                    <Divider />

                                                    {/*  COMMENT  */}
                                                    <FormControl
                                                        margin="normal"
                                                        className={formsClasses.share}
                                                        fullWidth
                                                    >
                                                        <FormLabel>
                                                            <Trans i18nKey="form.imageComment" />
                                                        </FormLabel>
                                                        <TextField
                                                            name={'photos[' + index + '].comment'}
                                                            multiline={true}
                                                            maxRows={9}
                                                            variant="outlined"
                                                        />
                                                    </FormControl>

                                                    <Divider />
                                                    {/*  SHARING  */}
                                                    <ShareImage
                                                        id={index}
                                                        isFacebookLinked={
                                                            props.values?.isFacebookShared ?? true
                                                        }
                                                        isInstagramLinked={
                                                            props.values?.isInstagramShared ?? true
                                                        }
                                                        isGoogleLinked={props.values?.isGoogleShared ?? true}
                                                        isTwitterLinked={
                                                            props.values?.isTwitterShared ?? true
                                                        }
                                                        showTwitter={showTwitter}
                                                        showInstagram={showInstagram}
                                                        showGoogle
                                                        showFacebook
                                                        sm={6}
                                                        xs={12}
                                                    />
                                                </div>
                                            )}
                                        </DraggableImage>
                                    ))}
                                    {images.length < (canPublishMultiplePhoto ? 10 : 1) && (
                                        <Grid item {...gridSize}>
                                            <Uploader
                                                className={clsx(formsClasses.uploader, {
                                                    [formsClasses.uploaderMargin]: images.length > 1,
                                                })}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                                {hasError && <FormHelperText error={hasError}>{error}</FormHelperText>}
                            </FormControl>
                        </>
                    )}
                </SortedImagesField>
            )}
        </FormSpy>
    )
}

export default PhotoContainer
